import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useContext,} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import WorkspaceContext from "../../WorkspaceContext";
import {formatNumber} from "../../../../helpers/number";
import {useCourseForm} from "../../../AppLocals/Course/CourseClass/useCourseForm";
import {getType} from "../../../../apis/model/base";
import {CourseActionTypes, StudentActionTypes} from "../../../AppLocals/Course";
import {useStudentForm} from "../../../AppLocals/Course/CourseClass/useStudentForm";
import {Avatar} from "antd";
import {FaChalkboard, FaUserGraduate, FaUserTie} from "react-icons/fa6";
import {ClassroomSvg} from "../../../../components/Svgs/ClassroomSvg";

const StatCard = ({title, value, secondary_value, color, icon, style, href}) => {
    const t = useTranslate()
    return (
        <Link to={href}
              style={style}
              className="px-4 py-3 flex items-center justify-between border border-color-100 background shadow-md rounded-xl no-underline cursor-pointer">
            <div className="flex flex-col flex-1">
                <p className="font-medium text-sm text-color-300 tracking-wider">{title}</p>
                <div className="mt-1 flex justify-end">
                    <Avatar
                        size={40}
                        className={`flex flex-center bg-${color}-100`}
                        icon={icon}
                    />
                </div>
                <div className="flex items-baseline gap-1 leading-snug">
                    <p className="text-2xl font-bold text-gray-900 tracking-wide">{value}</p>
                    {!!secondary_value && (
                        <p className="text-base font-medium text-color-500 tracking-wide">{` / ${secondary_value}`}</p>
                    )}
                    <p className="ml-1 text-color-400 tracking-wide italic">
                        {t('listed products')}
                    </p>
                </div>
            </div>
        </Link>
    )
}


const CourseDashboard = ({
                             owner_id,
                             owner_type,
                             filter_actions = {
                                 sortBy: 'sort-by',
                                 keyword: 'keyword',
                             },
                         }) => {
    const t = useTranslate()

    const history = useHistory()

    const location = useLocation()

    const {isSm} = useContext(LayoutContext)
    const {organization} = useContext(WorkspaceContext);

    const {
        modal: courseModal,
        handleAction: handleCourseAction,
        onCancel: onCourseCancel,
    } = useCourseForm({owner_id: organization?.id, owner_type: getType(organization)})
    const {
        modal: studentModal,
        handleAction: handleStudentAction,
        onCancel: onStudentCancel,
    } = useStudentForm({owner_id: organization?.id, owner_type: getType(organization)})


    return (
        <div className="flex flex-col flex-1 gap-y-3">
            <div className="flex flex-col gap-6">
                <h1 className="text-xl font-bold text-primary uppercase flex items-center">
                    {t("overview")}
                </h1>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                        title={t("total courses")}
                        value={formatNumber(organization?.workspace_settings?.courses?.statistics?.active_course_count)}
                        secondary_value={formatNumber(organization?.workspace_settings?.courses?.statistics?.course_count)}
                        color="indigo" icon={<FaChalkboard size={20} className="text-secondary"/>}
                        style={{
                            border: '1px solid var(--color-secondary-300)',
                            boxShadow: '0 2px 4px 0px var(--color-secondary-200)',
                        }}
                        href={`/workspace/course?rel=course`}
                    />
                    <StatCard
                        title={t("number of classes")}
                        value={formatNumber(organization?.workspace_settings?.courses?.statistics?.active_course_class_count)}
                        secondary_value={formatNumber(organization?.workspace_settings?.courses?.statistics?.course_class_count)}
                        color="orange" icon={<ClassroomSvg size={20} className="text-orange-500"/>}
                        style={{
                            border: '1px solid rgba(253, 186, 116, 1)',
                            boxShadow: '0 2px 4px 0px rgba(254, 215, 170, 1)',
                        }}
                        href={`/workspace/course?rel=course`}
                    />
                    <StatCard
                        title={t("total students")}
                        value={formatNumber(organization?.workspace_settings?.courses?.statistics?.active_student_count)}
                        secondary_value={formatNumber(organization?.workspace_settings?.courses?.statistics?.student_count)}
                        color="green" icon={<FaUserGraduate size={20} className="text-green-500"/>}
                        style={{
                            border: '1px solid rgba(106, 187, 141, 1)',
                            boxShadow: '0 2px 4px 0px rgba(198, 246, 213, 1)',
                        }}
                        href={`/workspace/course?rel=student`}
                    />
                    <StatCard
                        title={t("active teachers")}
                        value={formatNumber(organization?.workspace_settings?.courses?.statistics?.active_teacher_count)}
                        secondary_value={formatNumber(organization?.workspace_settings?.courses?.statistics?.teacher_count)}
                        color="red" icon={<FaUserTie size={20} className="text-red-500"/>}
                        style={{
                            border: '1px solid rgba(249, 159, 159, 1)',
                            boxShadow: '0 2px 4px 0px rgba(249, 159, 159, 1)',
                        }}
                        href={`/workspace/course?rel=teacher`}
                    />
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Quick Action: Create Course */}
                    <div
                        style={{
                            boxShadow: '0 -2px 6px -2px rgba(0, 0, 0, 0.08), 0 8px 12px -4px rgba(0, 0, 0, 0.1)',
                            borderRight: '1px solid var(--border-color-50)',
                        }}
                        className="p-6 flex flex-col background rounded-xl shadow-lg border-l-4 border-indigo-500">
                        <h3 className="mb-3 flex items-center text-xl font-bold text-gray-800 tracking-wide">
                            <FaChalkboard size={20} className="mr-2 text-indigo-500"/>
                            {t("initialize course")}
                        </h3>
                        <p className="flex-1 mb-4 text-color-300 tracking-wider">
                            {t("start setting up your new training content")}
                        </p>
                        <button
                            onClick={() => {
                                handleCourseAction(CourseActionTypes.COURSE_CREATE)
                            }}
                            className="w-full px-6 py-2 bg-indigo-500 text-white rounded-lg shadow-md hover:bg-indigo-700 transition font-medium">
                            + {t("create new course")}
                        </button>
                    </div>

                    {/* Quick Action: Register Student */}
                    <div
                        style={{
                            boxShadow: '0 -2px 6px -2px rgba(0, 0, 0, 0.08), 0 8px 12px -4px rgba(0, 0, 0, 0.1)',
                            borderRight: '1px solid var(--border-color-50)',
                        }}
                        className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
                        <h3 className="mb-3 flex items-center text-xl font-bold text-gray-800 tracking-wide">
                            <FaUserGraduate size={20} className="mr-2 text-green-500"/>
                            {t("register student")}
                        </h3>
                        <p className="flex-1 mb-4 text-color-300 tracking-wider">
                            {t("add a new student to the system or import a list")}
                        </p>
                        <button
                            onClick={() => {
                                handleStudentAction(StudentActionTypes.STUDENT_CREATE)
                            }}
                            className="w-full px-6 py-2 bg-green-500 text-white rounded-lg shadow-md hover:bg-green-700 transition font-medium">
                            + {t("add student")}
                        </button>
                    </div>
                </div>
            </div>
            {courseModal}
            {studentModal}
        </div>
    )
}


export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(CourseDashboard)
